import { useCallback, useEffect, useState } from "react";
import { Check, Clipboard, Edit3, Save, X } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { copyTextSafely } from "@/lib/clipboard";

export const PromptResult = ({ title, prompt, onSave, testIdPrefix = "prompt-result" }) => {
  const [currentPrompt, setCurrentPrompt] = useState(prompt || "");
  const [draftPrompt, setDraftPrompt] = useState(prompt || "");
  const [isEditing, setIsEditing] = useState(false);
  const [showSaveName, setShowSaveName] = useState(false);
  const [promptName, setPromptName] = useState("");

  useEffect(() => {
    setCurrentPrompt(prompt || "");
    setDraftPrompt(prompt || "");
    setIsEditing(false);
    setShowSaveName(false);
    setPromptName("");
  }, [prompt]);

  const copyPrompt = useCallback(async () => {
    if (!currentPrompt) return;
    const copied = await copyTextSafely(currentPrompt);
    if (copied) {
      toast.success("Prompti kopioitu!");
    } else {
      toast.error("Kopiointi epäonnistui. Kopioi prompti käsin tekstikentästä.");
    }
  }, [currentPrompt]);

  const startEditing = useCallback(() => {
    setDraftPrompt(currentPrompt);
    setIsEditing(true);
    setShowSaveName(false);
  }, [currentPrompt]);

  const saveEdits = useCallback(() => {
    setCurrentPrompt(draftPrompt);
    setIsEditing(false);
    toast.success("Muutokset tallennettu esikatseluun");
  }, [draftPrompt]);

  const cancelEdits = useCallback(() => {
    setDraftPrompt(currentPrompt);
    setIsEditing(false);
  }, [currentPrompt]);

  const confirmSave = useCallback(() => {
    if (!currentPrompt || !onSave) return;
    onSave({ title: promptName, prompt: currentPrompt });
    setPromptName("");
    setShowSaveName(false);
  }, [currentPrompt, onSave, promptName]);

  return (
    <section className="rounded-[1.75rem] border border-[#E8E5D9] bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:p-7" data-testid={`${testIdPrefix}-section`}>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-extrabold uppercase text-[#D97757]" data-testid={`${testIdPrefix}-eyebrow`}>Valmis kopioitavaksi</p>
          <h2 className="font-heading text-2xl font-bold text-[#2C2F2E]" data-testid={`${testIdPrefix}-title`}>{title}</h2>
        </div>
        <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-actions`}>
          {!isEditing ? (
            <button
              type="button"
              onClick={startEditing}
              disabled={!currentPrompt}
              className="inline-flex items-center gap-2 rounded-full bg-[#F5F3EC] px-4 py-3 text-sm font-bold text-[#2C2F2E] transition-transform duration-200 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid={`${testIdPrefix}-edit-button`}
            >
              <Edit3 className="h-4 w-4" aria-hidden="true" />
              Muokkaa promptia
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={saveEdits}
                disabled={!draftPrompt.trim()}
                className="inline-flex items-center gap-2 rounded-full bg-[#4A6753] px-4 py-3 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid={`${testIdPrefix}-save-edits-button`}
              >
                <Check className="h-4 w-4" aria-hidden="true" />
                Tallenna muutokset
              </button>
              <button
                type="button"
                onClick={cancelEdits}
                className="inline-flex items-center gap-2 rounded-full bg-[#F5F3EC] px-4 py-3 text-sm font-bold text-[#2C2F2E] transition-transform duration-200 hover:-translate-y-0.5"
                data-testid={`${testIdPrefix}-cancel-edits-button`}
              >
                <X className="h-4 w-4" aria-hidden="true" />
                Peruuta muokkaus
              </button>
            </>
          )}
          <button
            type="button"
            onClick={copyPrompt}
            disabled={!currentPrompt || isEditing}
            className="inline-flex items-center gap-2 rounded-full bg-[#4A6753] px-4 py-3 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid={`${testIdPrefix}-copy-button`}
          >
            <Clipboard className="h-4 w-4" aria-hidden="true" />
            Kopioi prompti
          </button>
          <button
            type="button"
            onClick={() => setShowSaveName((current) => !current)}
            disabled={!currentPrompt || isEditing}
            className="inline-flex items-center gap-2 rounded-full bg-[#F5F3EC] px-4 py-3 text-sm font-bold text-[#2C2F2E] transition-transform duration-200 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid={`${testIdPrefix}-save-button`}
          >
            <Save className="h-4 w-4" aria-hidden="true" />
            Tallenna promptikirjastoon
          </button>
        </div>
      </div>

      {showSaveName && currentPrompt && !isEditing && (
        <div className="mb-4 rounded-[1.25rem] border border-[#E8E5D9] bg-[#FDFCF8] p-4" data-testid={`${testIdPrefix}-save-name-panel`}>
          <label htmlFor={`${testIdPrefix}-save-name-input`} className="block text-sm font-bold text-[#2C2F2E]" data-testid={`${testIdPrefix}-save-name-label`}>Anna promptille nimi</label>
          <input
            id={`${testIdPrefix}-save-name-input`}
            value={promptName}
            onChange={(event) => setPromptName(event.target.value)}
            placeholder="Esim. AI-kuva: realistinen somekuva yrittäjästä"
            className="mt-2 w-full rounded-2xl border border-transparent bg-[#F5F3EC] px-4 py-3 text-sm text-[#2C2F2E] outline-none transition-colors duration-200 placeholder:text-[#5C6360]/55 focus:border-[#4A6753]/40 focus:bg-white focus:ring-4 focus:ring-[#4A6753]/10"
            data-testid={`${testIdPrefix}-save-name-input`}
          />
          <div className="mt-3 flex flex-wrap gap-2" data-testid={`${testIdPrefix}-save-name-actions`}>
            <button type="button" onClick={confirmSave} className="inline-flex items-center gap-2 rounded-full bg-[#4A6753] px-4 py-3 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5" data-testid={`${testIdPrefix}-confirm-save-button`}>
              <Save className="h-4 w-4" aria-hidden="true" /> Vahvista tallennus
            </button>
            <button type="button" onClick={() => setShowSaveName(false)} className="inline-flex items-center gap-2 rounded-full bg-[#F5F3EC] px-4 py-3 text-sm font-bold text-[#2C2F2E] transition-transform duration-200 hover:-translate-y-0.5" data-testid={`${testIdPrefix}-cancel-save-button`}>
              <X className="h-4 w-4" aria-hidden="true" /> Peruuta
            </button>
          </div>
        </div>
      )}

      {currentPrompt ? (
        isEditing ? (
          <textarea value={draftPrompt} onChange={(event) => setDraftPrompt(event.target.value)} className="min-h-72 w-full resize-y rounded-[1.25rem] border border-[#4A6753]/20 bg-[#F5F3EC] p-5 text-sm leading-7 text-[#2C2F2E] outline-none focus:bg-white focus:ring-4 focus:ring-[#4A6753]/10" data-testid={`${testIdPrefix}-edit-textarea`} />
        ) : (
          <pre className="min-h-72 whitespace-pre-wrap rounded-[1.25rem] bg-[#F5F3EC] p-5 text-sm leading-7 text-[#2C2F2E]" data-testid={`${testIdPrefix}-prompt-text`}>{currentPrompt}</pre>
        )
      ) : (
        <div className="grid min-h-72 place-items-center rounded-[1.25rem] bg-[#F5F3EC] p-8 text-center" data-testid={`${testIdPrefix}-empty-state`}>
          <div>
            <Check className="mx-auto mb-3 h-8 w-8 text-[#4A6753]" aria-hidden="true" />
            <p className="font-bold text-[#2C2F2E]" data-testid={`${testIdPrefix}-empty-title`}>Täytä kentät ja luo prompti</p>
            <p className="mt-1 text-sm text-[#5C6360]" data-testid={`${testIdPrefix}-empty-description`}>Valmis prompti ilmestyy tähän.</p>
          </div>
        </div>
      )}
    </section>
  );
};